import {
  AGE_GROUP_LABELS,
  AGE_GROUPS,
  APPROVAL_METHODS,
  type AgeGroup,
  type ApprovalEventDto,
  type LeaderboardDto,
  type TechniqueAttemptStatsDto,
  type AuditEventDto,
  type StudentSummaryDto,
  type UserDto,
} from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import { useTechniques } from '../api/hooks';
import { DataTable } from '../components/DataTable';
import {
  Badge,
  Card,
  ErrorMessage,
  formatDate,
  gradedWhen,
  PageHeader,
  ProgressBar,
  GradeBadge,
  gradeEventText,
} from '../components/ui';

export function ApprovalsPage() {
  const techniques = useTechniques();
  const [techniqueId, setTechniqueId] = useState('');
  const [instructorId, setInstructorId] = useState('');
  const [method, setMethod] = useState('');
  const staff = useQuery({
    queryKey: ['admin', 'staff-list'],
    queryFn: () =>
      api
        .request<{ items: UserDto[] }>('/admin/users?limit=500')
        .then((r) => r.items.filter((u) => u.role === 'instructor' || u.role === 'owner')),
    retry: false,
  });
  const params = new URLSearchParams({ limit: '200' });
  if (techniqueId) params.set('techniqueId', techniqueId);
  if (instructorId) params.set('instructorId', instructorId);
  if (method) params.set('method', method);
  const events = useQuery({
    queryKey: ['approvals', params.toString()],
    queryFn: () =>
      api.request<{ items: ApprovalEventDto[] }>(`/approvals?${params}`).then((r) => r.items),
  });
  return (
    <>
      <PageHeader
        title="Grading history"
        subtitle="Every grade given, raised, lowered or removed, with who did it and when."
      />
      <Card
        actions={
          <div className="btn-row">
            <select
              aria-label="Filter by technique"
              value={techniqueId}
              onChange={(e) => setTechniqueId(e.target.value)}
            >
              <option value="">All techniques</option>
              {techniques.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {staff.data ? (
              <select
                aria-label="Filter by instructor"
                value={instructorId}
                onChange={(e) => setInstructorId(e.target.value)}
              >
                <option value="">All instructors</option>
                {staff.data.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            ) : null}
            <select
              aria-label="Filter by method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="">All methods</option>
              {APPROVAL_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <ErrorMessage error={events.error} />
        <DataTable
          rows={events.data}
          rowKey={(e) => e.id}
          empty="No grades match."
          columns={[
            { key: 'when', header: 'When', render: gradedWhen },
            {
              key: 'student',
              header: 'Student',
              render: (e) => <Link to={`/students/${e.student.id}`}>{e.student.name}</Link>,
            },
            { key: 'technique', header: 'Technique', render: (e) => e.technique.name },
            { key: 'by', header: 'Instructor', render: (e) => e.instructor?.name ?? '—' },
            {
              key: 'event',
              header: 'Grade',
              render: (e) => (
                <span className="grade-badge">
                  {e.grade ? <GradeBadge grade={e.grade} /> : <Badge value="rescinded" />}
                  {gradeEventText(e)}
                  {e.method && e.method !== 'qr' ? <Badge value={e.method} /> : null}
                </span>
              ),
            },
            { key: 'reason', header: 'Reason', render: (e) => e.reason ?? '' },
          ]}
        />
      </Card>
    </>
  );
}

export function ReportsPage() {
  const report = useQuery({
    queryKey: ['students', 'report'],
    queryFn: () =>
      api
        .request<{ items: StudentSummaryDto[] }>('/students?limit=500&status=active')
        .then((r) => r.items),
  });
  const rows = report.data
    ? [...report.data].sort((a, b) => b.progress.percent - a.progress.percent)
    : undefined;
  return (
    <>
      <PageHeader
        title="Progress reports"
        subtitle="Completion for active students across the active curriculum."
      />
      <Card>
        <ErrorMessage error={report.error} />
        <DataTable
          rows={rows}
          rowKey={(s) => s.user.id}
          columns={[
            {
              key: 'name',
              header: 'Student',
              render: (s) => <Link to={`/students/${s.user.id}`}>{s.user.name}</Link>,
            },
            {
              key: 'pct',
              header: 'Completion',
              render: (s) => (
                <ProgressBar percent={s.progress.percent} label={`${s.user.name} completion`} />
              ),
            },
            {
              key: 'count',
              header: 'Mastered',
              render: (s) => `${s.progress.mastered} / ${s.progress.total}`,
            },
            {
              key: 'remaining',
              header: 'Remaining',
              render: (s) => s.progress.total - s.progress.mastered,
            },
            { key: 'last', header: 'Last graded', render: (s) => formatDate(s.lastGradedAt) },
          ]}
        />
      </Card>
      <LeaderboardsCard />
      <RetestReport />
    </>
  );
}

/** The leaderboards students see in the app, per group (first name and last initial only). */
function LeaderboardsCard() {
  const [group, setGroup] = useState<AgeGroup>('adults');
  const boards = useQuery({
    queryKey: ['leaderboards', group],
    queryFn: () => api.request<LeaderboardDto>(`/leaderboards?group=${group}`),
  });
  const board = (title: string, entries: LeaderboardDto['furthest'], value: string) => (
    <div>
      <h3>{title}</h3>
      <DataTable
        rows={entries}
        rowKey={(e) => `${e.rank}-${e.name}`}
        empty="Nobody yet."
        columns={[
          { key: 'rank', header: '#', width: 40, render: (e) => e.rank },
          { key: 'name', header: 'Student', render: (e) => e.name },
          {
            key: 'value',
            header: value,
            render: (e) => (value === 'Completion' ? `${e.percent}%` : `+${e.recent}`),
          },
        ]}
      />
    </div>
  );
  return (
    <Card
      title="Leaderboards"
      actions={
        <select
          aria-label="Leaderboard group"
          value={group}
          onChange={(e) => setGroup(e.target.value as AgeGroup)}
        >
          {AGE_GROUPS.map((g) => (
            <option key={g} value={g}>
              {AGE_GROUP_LABELS[g]}
            </option>
          ))}
        </select>
      }
    >
      <p className="muted">
        What students see in the app for their own group, with first names and last initials only.
        To keep someone off the boards, untick Show on leaderboards on their page.
      </p>
      <ErrorMessage error={boards.error} />
      {boards.data ? (
        <div className="grid cols-2">
          {board('Furthest along', boards.data.furthest, 'Completion')}
          {board(
            `Growing fastest (last ${boards.data.windowDays} days)`,
            boards.data.fastest,
            'Mastered',
          )}
        </div>
      ) : null}
    </Card>
  );
}

/** Each grade is an attempt: how many tries mastering each technique takes. Hardest first. */
function RetestReport() {
  const stats = useQuery({
    queryKey: ['admin', 'reports', 'attempts'],
    queryFn: () =>
      api
        .request<{ items: TechniqueAttemptStatsDto[] }>('/admin/reports/attempts')
        .then((r) => r.items),
  });
  return (
    <Card title="Attempts and retests">
      <p className="muted">
        Every grade counts as an attempt, so a C, then a B, then an A is mastered on attempt 3.
        Techniques that take the most attempts are listed first. Active students only.
      </p>
      <ErrorMessage error={stats.error} />
      <DataTable
        rows={stats.data}
        rowKey={(s) => s.technique.id}
        empty="No grades yet."
        columns={[
          { key: 'name', header: 'Technique', render: (s) => s.technique.name },
          { key: 'students', header: 'Graded', render: (s) => s.students },
          { key: 'mastered', header: 'Mastered', render: (s) => s.mastered },
          {
            key: 'first',
            header: 'A on first try',
            render: (s) => (s.firstTryPercent === null ? '—' : `${s.firstTryPercent}%`),
          },
          {
            key: 'avg',
            header: 'Avg. attempts to A',
            render: (s) => s.averageAttemptsToMaster ?? '—',
          },
          { key: 'retesting', header: 'Still working on it', render: (s) => s.retesting },
        ]}
      />
    </Card>
  );
}

export function AuditPage() {
  const [action, setAction] = useState('');
  const events = useQuery({
    queryKey: ['admin', 'audit', action],
    queryFn: () =>
      api
        .request<{ items: AuditEventDto[] }>(
          `/admin/audit?limit=300${action ? `&action=${encodeURIComponent(action)}` : ''}`,
        )
        .then((r) => r.items),
  });
  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Append-only record of administrative and security-relevant actions."
      />
      <Card
        actions={
          <select
            aria-label="Filter by action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <option value="">All actions</option>
            <option value="grade.">Grades</option>
            <option value="user.">Users & membership</option>
            <option value="invitation.">Invitations</option>
            <option value="technique.">Techniques</option>
            <option value="curriculum.">Curriculum imports</option>
            <option value="video.">Videos</option>
            <option value="settings.">Settings</option>
            <option value="auth.">Sign-ins</option>
            <option value="session.">Session security</option>
          </select>
        }
      >
        <ErrorMessage error={events.error} />
        <DataTable
          rows={events.data}
          rowKey={(e) => e.id}
          columns={[
            { key: 'when', header: 'When', render: (e) => formatDate(e.createdAt) },
            {
              key: 'action',
              header: 'Action',
              render: (e) => <span className="mono">{e.action}</span>,
            },
            { key: 'actor', header: 'Actor', render: (e) => e.actor?.name ?? 'System' },
            {
              key: 'target',
              header: 'Target',
              render: (e) => (e.targetType ? `${e.targetType} ${e.targetId ?? ''}` : ''),
            },
            {
              key: 'meta',
              header: 'Details',
              render: (e) => (
                <span className="mono">
                  {Object.keys(e.metadata).length ? JSON.stringify(e.metadata) : ''}
                </span>
              ),
            },
            { key: 'ip', header: 'IP', render: (e) => e.ip ?? '' },
          ]}
        />
      </Card>
    </>
  );
}
